import {
  LoadSpacingScenario,
  LoadSpacingConfiguration
} from '@hconnect/tuc-processes/fields/loadSpacing/types'
import {useLoadSpacing} from '@hconnect/tuc-processes/fields/loadSpacing/useLoadSpacing'
import {Check} from '@mui/icons-material'
import {Box, Typography, Radio, List, ListItem, ListItemButton} from '@mui/material'
import React, {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

interface SpacingOrFlowRateSelectorProps {
  scenario: LoadSpacingScenario
  loadSpacingConfiguration: LoadSpacingConfiguration
}

/**
 * Component for selecting between spacing and flow rate with list-based value selection
 * Redesigned to match Figma specifications with radio button tiles and scrollable list
 */
export const SpacingOrFlowRateSelector: React.FC<SpacingOrFlowRateSelectorProps> = ({
  scenario,
  loadSpacingConfiguration
}) => {
  const {t} = useTranslation()
  const {spacingOrFlowRate, setSpacingOrFlowRate} = useLoadSpacing()

  // Determine current selection type
  const currentType = spacingOrFlowRate?.type || (scenario.loadSpace ? 'loadSpace' : 'flowRate')

  // Get current settings based on type
  const currentSettings = useMemo(() => {
    if (!loadSpacingConfiguration.loadSpacing.isEnabled) {
      return
    }

    return currentType === 'loadSpace'
      ? loadSpacingConfiguration.loadSpacing.loadSpace
      : loadSpacingConfiguration.loadSpacing.flowRate
  }, [currentType, loadSpacingConfiguration])

  // Get current value
  const currentValue = useMemo(() => {
    if (!spacingOrFlowRate) return currentSettings?.default ?? undefined

    if (currentType === 'loadSpace' && spacingOrFlowRate.type === 'loadSpace') {
      return spacingOrFlowRate.loadSpace
    }
    if (currentType === 'flowRate' && spacingOrFlowRate.type === 'flowRate') {
      return spacingOrFlowRate.flowRate
    }

    return currentSettings?.default ?? undefined
  }, [spacingOrFlowRate, currentType, currentSettings])

  // Handle type change (spacing vs flow rate)
  const handleTypeChange = useCallback(
    (newType: 'loadSpace' | 'flowRate') => {
      if (newType === currentType || !loadSpacingConfiguration.loadSpacing.isEnabled) return

      const settings =
        newType === 'loadSpace'
          ? loadSpacingConfiguration.loadSpacing.loadSpace
          : loadSpacingConfiguration.loadSpacing.flowRate

      // Set default value when switching type
      if (settings) {
        setSpacingOrFlowRate(settings.default, newType)
      }
    },
    [currentType, loadSpacingConfiguration, setSpacingOrFlowRate]
  )

  // Handle value selection
  const handleValueSelect = useCallback(
    (value: number) => {
      setSpacingOrFlowRate(value, currentType)
    },
    [setSpacingOrFlowRate, currentType]
  )

  // Generate list of values based on current settings
  const valueOptions = useMemo(() => {
    if (!currentSettings) return []

    const options: number[] = []
    for (let i = currentSettings.min; i <= currentSettings.max; i += currentSettings.step) {
      options.push(i)
    }
    return options
  }, [currentSettings])

  if (!currentSettings) {
    return null
  }

  // Create toggle button options
  const shouldShowToggle = scenario.loadSpace && scenario.flowRate

  return (
    <Box sx={{mb: 4}}>
      {/* Radio button selection tiles for spacing vs flow rate */}
      {shouldShowToggle && (
        <Box sx={{display: 'flex', gap: 2, mb: 4, flexDirection: 'row', flexWrap: 'wrap'}}>
          {/* Spacing tile */}
          <Box
            onClick={() => handleTypeChange('loadSpace')}
            sx={{
              flex: 1,
              border: currentType === 'loadSpace' ? '2px solid #016ad4' : '1px solid #d1d9e0',
              borderRadius: '8px',
              padding: '12px 24px',
              backgroundColor: currentType === 'loadSpace' ? '#f2f8fd' : 'transparent',
              width: '100%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '8px',
              position: 'relative'
            }}
            data-test-id="intervals-spacing-toggle"
          >
            <Radio
              checked={currentType === 'loadSpace'}
              sx={{
                color: '#d1d9e0',
                '&.Mui-checked': {
                  color: '#016ad4'
                },
                padding: 0
              }}
            />
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#29333d'
              }}
            >
              {t('orderIntake.rmc.steps.intervals.spacing', 'loadSpace')}
            </Typography>
          </Box>

          {/* Flow rate tile */}
          <Box
            onClick={() => handleTypeChange('flowRate')}
            sx={{
              flex: 1,
              border: currentType === 'flowRate' ? '2px solid #016ad4' : '1px solid #d1d9e0',
              borderRadius: '8px',
              padding: '12px 24px',
              backgroundColor: currentType === 'flowRate' ? '#f2f8fd' : 'transparent',
              width: '100%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '8px',
              position: 'relative'
            }}
            data-test-id="intervals-flowrate-toggle"
          >
            <Radio
              checked={currentType === 'flowRate'}
              sx={{
                color: '#d1d9e0',
                '&.Mui-checked': {
                  color: '#016ad4'
                },
                padding: 0
              }}
            />
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#29333d'
              }}
            >
              {t('orderIntake.rmc.steps.intervals.flowRate', 'Flow rate')}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Scrollable list of values */}
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#ffffff',
          overflow: 'auto'
        }}
      >
        <List sx={{padding: 0}} data-test-id={`intervals-${currentType}-list`}>
          {valueOptions.map((value, index) => {
            const isSelected = value === currentValue
            const isLast = index === valueOptions.length - 1

            return (
              <ListItem
                key={value}
                sx={{
                  padding: 0,
                  borderBottom: !isLast ? '1px solid #e8ecf0' : 'none'
                }}
              >
                <ListItemButton
                  onClick={() => handleValueSelect(value)}
                  sx={{
                    padding: '12px 16px',
                    backgroundColor: isSelected ? '#f2f8fd' : '#ffffff',
                    '&:hover': {
                      backgroundColor: '#f2f8fd'
                    },
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: '48px'
                  }}
                  data-test-id={`intervals-${currentType}-option-${value}`}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: '#29333d'
                    }}
                  >
                    {value} {currentSettings.uom}
                  </Typography>
                  {isSelected && (
                    <Check
                      sx={{
                        fontSize: '16px',
                        color: '#016ad4'
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Box>
    </Box>
  )
}
